import { Avatar, Chip, Link } from "@heroui/react";

import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { initials } from "@/src/libs/initials";
import { FLAG_KEY, FLAG_TONE } from "@/src/libs/moderation.type";
import type { AdminAccount } from "@/src/pages/Accounts/accounts.type";
import { ROUTES } from "@/src/routes/paths";

/**
 * Une ligne de la liste des comptes.
 *
 * ⚠️ **Toute la ligne est cliquable, mais c'est le pseudo qui porte le lien.**
 * Un `onClick` posé sur la carte donnerait une zone cliquable invisible au
 * clavier et muette pour un lecteur d'écran ; l'`absolute inset-0` sur le lien
 * étend sa surface sans lui retirer son rôle ni son nom accessible.
 *
 * L'e-mail est affiché en clair : c'est le seul écran de l'admin qui en dispose
 * pour un tiers, et c'est justement ce qu'on vient y chercher.
 */
export function AccountRow({ account }: { account: AdminAccount }) {
  const t = useTranslations("accounts");
  const tm = useTranslations("moderation");
  const tg = useTranslations("geo");
  const { locale } = useLocale();

  const registered = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(account.createdAt));

  return (
    <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg p-3 transition-colors hover:bg-surface-hover">
      <Avatar className="size-9 shrink-0" size="sm">
        {account.avatarUrl ? (
          <Avatar.Image alt="" src={account.avatarUrl} />
        ) : null}
        <Avatar.Fallback className="text-xs">
          {initials(account.username)}
        </Avatar.Fallback>
      </Avatar>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          {/*
            ⚠️ **`static` n'est pas décoratif.** `.link` pose
            `position: relative` (cf. § HeroUI du CLAUDE.md) : sans cette
            classe, l'`after:inset-0` s'ancre sur le lien lui-même et n'étend
            rien du tout — seul le pseudo reste cliquable, ce qui est
            exactement le bug qu'on corrige ici. `static` rend le lien
            transparent au positionnement, et la surface se cale enfin sur la
            ligne, qui porte le `relative`.
          */}
          <Link
            className="static text-sm font-medium after:absolute after:inset-0"
            href={ROUTES.account(account.id)}
          >
            {account.username}
          </Link>

          {/* Le rôle est ce qui change tout sur une fiche de modération : un
              administrateur ne se sanctionne pas comme un joueur. */}
          {account.roles
            .filter((role) => role !== "user")
            .map((role) => (
              <Chip color="warning" key={role} size="sm">
                {role}
              </Chip>
            ))}

          {account.verified ? null : (
            <Chip size="sm" variant="secondary">
              {t("unverified")}
            </Chip>
          )}

          {/* ⚠️ Un compte désactivé se voit **sans ouvrir sa fiche** : c'est ce
              qui évite de rouvrir un dossier déjà tranché. */}
          {account.disabledAt ? (
            <Chip color="danger" size="sm">
              {tm("state-disabled")}
            </Chip>
          ) : null}

          {/* Les drapeaux d'un collègue, en pastilles : la liste doit dire ce
              que la modération sait déjà d'un compte, sinon chaque
              signalement se juge sans mémoire. */}
          {account.flags.map((flag) => (
            <Chip color={FLAG_TONE[flag]} key={flag} size="sm">
              {tm(FLAG_KEY[flag])}
            </Chip>
          ))}
        </div>

        <span className="truncate text-xs text-muted">{account.email}</span>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs text-muted">
        <span>{registered}</span>

        {/* La distance ne vaut que si une recherche géographique l'a produite ;
            ailleurs le serveur la met à `null`, et non à zéro. */}
        {account.distance === null ? (
          account.locationLabel ? (
            <span className="max-w-52 truncate">{account.locationLabel}</span>
          ) : null
        ) : (
          <span>
            {tg("distance", { km: Math.round(account.distance) })}
            {account.locationLabel ? ` · ${account.locationLabel}` : null}
          </span>
        )}
      </div>
    </div>
  );
}
